import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(0);

  // Fetch credit balance
  const refreshCredits = useCallback(async () => {
    try {
      const data = await authService.getMyCredits();
      setCredits(data.credits);
    } catch {
      // ignore - user might not be logged in
    }
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const storedUser = authService.getStoredUser();
    const token = authService.getToken();

    if (storedUser && token) {
      setUser(storedUser);
      // Verify token is still valid by calling /auth/me
      authService.getMe()
        .then((freshUser) => {
          setUser(freshUser);
          setCredits(freshUser.credits ?? 0);
          localStorage.setItem('xalent_user', JSON.stringify(freshUser));
        })
        .catch(() => {
          // Token is invalid/expired
          authService.logout();
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Refresh credits when user changes
  useEffect(() => {
    if (user) {
      refreshCredits();
    }
  }, [user, refreshCredits]);

  const login = useCallback(async (email, password) => {
    const { user: loggedInUser } = await authService.login(email, password);
    setUser(loggedInUser);
    setCredits(loggedInUser.credits ?? 0);
    return loggedInUser;
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
    setCredits(0);
  }, []);

  const isAdmin = user?.role === 'admin';

  const value = {
    user,
    loading,
    login,
    logout,
    isAdmin,
    isAuthenticated: !!user,
    credits,
    refreshCredits,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
